import React, {useEffect, useState} from "react";
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    Cell, Label,
    Legend,
    Line,
    LineChart,
    Pie,
    PieChart,
    PolarAngleAxis,
    PolarGrid,
    PolarRadiusAxis,
    Radar,
    RadarChart,
    ReferenceArea,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from "recharts";

import mystyles from './styles/graphics.module.css';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faClipboard, faCog} from "@fortawesome/free-solid-svg-icons";
import {format} from 'date-fns';
import {getStateLabels} from './utils';

function CustomToolTip({ active, payload, label, frequency }) {
    const variable = JSON.parse(localStorage.getItem('variable'));
    const unit = variable?.variable?.unit;
    const varType = variable?.variable?.type;

    // getStateLabels tolera un localStorage viejo (anterior a las etiquetas):
    // en ese caso devuelve 'True'/'False', igual que antes.
    const {trueLabel, falseLabel} = getStateLabels(variable);

    const formatValue = (value) => {
        // Tipo 5: Boolean. /api/graphs/ manda '0'/'1', de ahí el parseInt.
        if (varType === 5) {
            return parseInt(value) === 0 ? falseLabel : trueLabel;
        }
        // Tipo 3: String
        if (varType === 3) {
            return value;
        }
        // Números: limitar a 4 decimales máximo
        if (!isNaN(parseFloat(value)) && isFinite(value)) {
            const precision = Math.min(variable?.precision ?? 2, 4);
            return parseFloat(value).toFixed(precision);
        }
        return value;
    };

    if (active) {
        if(label === undefined) return <div>-</div>

        const [datePart, timePart] = label.split(', ');
        if (!datePart || !timePart) {
            console.error("Invalid payload format:", label);
            return "-";
        }
        const [day, month, year] = datePart.split('/').map(Number);
        const timePartSplit = timePart.split(':');
        const [hour, minute, second] = timePartSplit.map(Number);

        const amOrPm = timePartSplit[2].trim().slice(-2);

        const hourString = (hour < 10 ? '0' : '') + hour + ':' + (minute < 10 ? '0' : '') + minute + amOrPm;
        const dateString = (day < 10 ? '0' : '') + day + '/' + (month < 10 ? '0' : '') + month + '/' + year;

        return (
            <div style={{backgroundColor: '#FFF', border: '1px solid #000', borderRadius: '6px'}} className={'p-1'}>
                <div>Date: {(frequency === '1h' || frequency === '24h') ? hourString : (frequency === '7d' ? dateString + "," + hourString : dateString)}</div>
                {payload && <div>Value: {formatValue(payload[0].value)}<b>{unit}</b></div>}
            </div>
        );
    }

    return null;
}

function CustomYAxis({ x, y, payload, unit }) {
    return (
        <g transform={`translate(${x},${y})`}>
            <text x={0} y={0} dy={16} textAnchor="end" fill="#000">
                {payload.value}<tspan fontWeight="bold">{unit}</tspan>
            </text>
        </g>
    )
}

function CustomXAxis({ x, y, payload, frequency }) {
    const [datePart, timePart] = payload.value.split(', ');
    if (!datePart || !timePart) {
        console.error("Invalid payload format:", payload.value);
        return payload.value;
    }
    const [day, month, year] = datePart.split('/').map(Number);
    const timePartSplit = timePart.split(':');
    const [hour, minute, second] = timePartSplit.map(Number);

    const amOrPm = timePartSplit[2].trim().slice(-2);

    const hourString = (hour < 10 ? '0' : '') + hour + ':' + (minute < 10 ? '0' : '') + minute + amOrPm;
    const dateString = (day < 10 ? '0' : '') + day + '/' + (month < 10 ? '0' : '') + month + '/' + year;
    return (
        <text x={x} y={y} dy={16} textAnchor="middle" fill="#666">
            {(frequency === '1h' || frequency === '24h' || frequency === '7d') ? hourString : dateString}
        </text>
    )
}

export default function ChartComponent({name, chartType, frequency, data, setShowModal, setShowDashboardModal, isDashboardItem, setSelectedGraph}) {
    const [chartData, setChartData] = useState([]);
    const variable = JSON.parse(localStorage.getItem('variable'));
    const unit = variable?.variable?.unit;
    const [minMax, setMinMax] = useState([-100, 100]);
    // Etiquetas del eje del gráfico de escalones (ej. Abierto/Cerrado)
    const {trueLabel: stepTrueLabel, falseLabel: stepFalseLabel} = getStateLabels(variable);

    const COLORS = ["#8884d8", "#82ca9d", "#FFBB28", "#FF8042", "#AF19FF", "#00C49F", "#FFC658", "#FF4842", "#0088FE", "#A300D6", "#FFDD00", "#FF6E00"];

    const formatTimestamp = (timestamp) => {
        if (timestamp == null) return "-";
        const date = new Date(timestamp);
        return format(date, 'dd/MM/yyyy, hh:mm:ss a');
    }

    useEffect(() => {
        data.forEach(element => element.name = formatTimestamp(element.x));

        data.sort((a, b) => {
            const timestampA = new Date(a.x).getTime();
            const timestampB = new Date(b.x).getTime();
            return timestampA - timestampB;
        });

        let minValue = Math.min(...data.map(item => item.value));
        let maxValue = Math.max(...data.map(item => item.value));

        minValue = Math.min(minValue, variable.medium_threshold_2);
        maxValue = Math.max(maxValue, variable.medium_threshold);

        let delta = minValue * 0.1;
        if(delta < 0) minValue += delta;
        else minValue -= delta;

        delta = maxValue * 0.1;
        if(delta < 0) maxValue -= delta;
        else maxValue += delta;

        minValue = parseFloat(minValue.toFixed(2));
        maxValue = parseFloat(maxValue.toFixed(2));

        setMinMax([minValue, maxValue]);
        setChartData(data);
    }, [frequency, data]);

    const [isHovered, setIsHovered] = useState(false);

    const thresholdDraw = () => {
        if(!variable) return null;
        // No dibujar thresholds si disable_alerts está activado
        if(variable.disable_alerts) return null;
        const low1 = variable?.low_threshold + "" + variable.variable.unit;
        const low2 = variable?.low_threshold_2 + "" + variable.variable.unit;
        const medium1 = variable?.medium_threshold + "" + variable.variable.unit;
        const medium2 = variable?.medium_threshold_2 + "" + variable.variable.unit;

        const hover = () => { setIsHovered(true);}
        const noHover = () => { setIsHovered(false);}

        return (
            <>
                <ReferenceLine isFront={true} y={variable.low_threshold} stroke={'#ffbf00'} strokeWidth={4} onMouseEnter={hover} onMouseLeave={noHover}>
                    {isHovered && (
                        <Label
                            enableBackground={1}
                            position="left"
                            value={low1}
                            fill="#ffbf00"
                            fontSize={14}
                        />
                    )}
                </ReferenceLine>

                <ReferenceLine isFront={true} y={variable.low_threshold_2} stroke={'#ffbf00'} strokeWidth={4} onMouseEnter={hover} onMouseLeave={noHover}>
                    {isHovered && (
                        <Label
                            enableBackground={1}
                            position="left"
                            value={low2}
                            fill="#ffbf00"
                            fontSize={14}
                        />
                    )}
                </ReferenceLine>

                <ReferenceLine isFront={true} y={variable.medium_threshold} stroke="red" strokeWidth={4} onMouseEnter={hover} onMouseLeave={noHover}>
                    {isHovered && (
                        <Label
                            position="left"
                            value={medium1}
                            fill="red"
                            fontSize={14}
                        />
                    )}
                </ReferenceLine>
                <ReferenceLine isFront={true} y={variable.medium_threshold_2} stroke="red" strokeWidth={4} onMouseEnter={hover} onMouseLeave={noHover}>
                    {isHovered && (
                        <Label
                            enableBackground={1}
                            position="left"
                            value={medium2}
                            fill="red"
                            fontSize={14}
                        />
                    )}
                </ReferenceLine>

                <ReferenceArea y1={variable.low_threshold} y2={variable.low_threshold_2} fill={'rgba(190,245,175,0.65)'} strokeOpacity={0}/>
            </>
        )
    }

    const chartFunction = () => {
        switch (chartType) {
            case 'line':
                return (
                    <LineChart data={chartData} width={'400'} margin={{top: 0, right: 0, left: 50, bottom: 10}}>
                        <XAxis
                            dataKey="name"
                            tick={<CustomXAxis frequency={frequency}/>}
                        />
                        <YAxis
                            domain={minMax}
                            padding={{bottom: 30}}
                            tick={<CustomYAxis unit={unit}/>}
                        />
                        <CartesianGrid stroke="#ccc" strokeDasharray="3 3" />
                        <Tooltip content={<CustomToolTip chartData={chartData} frequency={frequency}/>} />
                        <Line type="monotone" dataKey="value" stroke="#8884d8" />
                        {thresholdDraw()}
                    </LineChart>
                );
            case 'bar':
                return (
                    <BarChart data={chartData} margin={{top: 0, right: 0, left: 50, bottom: 10}}>
                        <XAxis
                            dataKey="name"
                            tick={<CustomXAxis frequency={frequency}/>}
                        />
                        <YAxis
                            domain={minMax}
                            padding={{bottom: 30}}
                            tick={<CustomYAxis unit={unit}/>}
                        />
                        <CartesianGrid stroke="#ccc" strokeDasharray="3 3" />
                        <Tooltip content={<CustomToolTip chartData={chartData} frequency={frequency}/>} />
                        <Bar dataKey="value">
                            {chartData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                        </Bar>
                        {thresholdDraw()}
                    </BarChart>
                );
            case 'area':
                return (
                    <AreaChart data={chartData} margin={{top: 0, right: 0, left: 50, bottom: 10}}>
                        <XAxis
                            dataKey="name"
                            tick={<CustomXAxis frequency={frequency}/>}
                        />
                        <YAxis
                            domain={minMax}
                            padding={{bottom: 30}}
                            tick={<CustomYAxis unit={unit}/>}
                        />
                        <CartesianGrid stroke="#ccc" strokeDasharray="3 3" />
                        <Tooltip content={<CustomToolTip chartData={chartData} frequency={frequency}/>} />
                        <Area type="monotone" dataKey="value" fill="#8884d8" stroke="#8884d8" />
                        {thresholdDraw()}
                    </AreaChart>
                );
            case 'pie':
                return (
                    <PieChart margin={{top: 0, right: 0, left: 50, bottom: 10}}>
                        <Pie data={chartData} dataKey="value" cx="50%" cy="50%" outerRadius={80} fill="#8884d8">
                            {chartData.map((entry, index) => (
                                <Cell
                                    key={`cell-${index}`}
                                    fill={COLORS[index % COLORS.length]}
                                />
                            ))}
                        </Pie>
                        <Tooltip content={<CustomToolTip chartData={chartData} frequency={frequency}/>} />
                        <Legend/>
                    </PieChart>
                );
            case 'radar':
                return (
                    <RadarChart data={chartData} margin={{top: 0, right: 0, left: 50, bottom: 10}}>
                        <PolarGrid />
                        <PolarAngleAxis dataKey="name" />
                        <PolarRadiusAxis />
                        <Radar name="Value" dataKey="value" stroke="#8884d8" fill="#8884d8" fillOpacity={0.6} />
                        <Tooltip />
                    </RadarChart>
                );
            case 'step':
                return (
                    <LineChart data={chartData} width={'400'} margin={{top: 0, right: 0, left: 50, bottom: 10}}>
                        <XAxis
                            dataKey="name"
                            tick={<CustomXAxis frequency={frequency}/>}
                        />
                        <YAxis
                            domain={[0, 1]}
                            ticks={[0, 1]}
                            tickFormatter={(v) => v === 0 ? stepFalseLabel : stepTrueLabel}
                            width={80}
                            padding={{bottom: 10, top: 10}}
                        />
                        <CartesianGrid stroke="#ccc" strokeDasharray="3 3" />
                        <Tooltip content={<CustomToolTip chartData={chartData} frequency={frequency}/>} />
                        <Line type="stepAfter" dataKey="value" stroke="#8884d8" strokeWidth={2} dot={true} />
                    </LineChart>
                );
            case 'timeline':
                return (
                    <div style={{
                        maxHeight: '400px',
                        overflowY: 'auto',
                        padding: '10px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '6px'
                    }}>
                        {chartData.length === 0 ? (
                            <div style={{textAlign: 'center', color: '#666', padding: '20px'}}>No data available</div>
                        ) : (
                            chartData.map((item, index) => (
                                <div key={index} style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '8px 12px',
                                    marginBottom: '4px',
                                    backgroundColor: '#fff',
                                    borderRadius: '4px',
                                    borderLeft: '4px solid #8884d8',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                                }}>
                                    <div style={{
                                        flex: '0 0 auto',
                                        marginRight: '12px',
                                        fontSize: '12px',
                                        color: '#666',
                                        minWidth: '140px'
                                    }}>
                                        {item.name}
                                    </div>
                                    <div style={{
                                        flex: '1',
                                        fontWeight: '500',
                                        color: '#333',
                                        wordBreak: 'break-word'
                                    }}>
                                        {item.value}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                );
            default:
                return (<div></div>);
        }
    }

    const doShowModal = () => {
        if(setSelectedGraph) {
            if(data.length > 0) {
                setSelectedGraph(data[0].variable_data_id);
            } else {
                return;
            }
        }
        setShowModal(true);
    }

    return (
        <div className={`${mystyles.chartContainer} mt-4`}>
            <div className={'d-flex justify-content-between align-content-center align-items-center pt-1 pb-1'}>
                <div className={`${mystyles.chartTitle}`}>{(data.length !== 0 ? data[0].device : '') + ' - ' + variable?.variable.name}</div>
                <div className={'d-flex'}>
                    {!isDashboardItem &&
                        <div role={'button'} className={'pe-2'}>
                            <FontAwesomeIcon icon={faClipboard} color={'#8A98A8'} onClick={() => setShowDashboardModal(true)}/>
                        </div>
                    }
                    <div className={'me-3'} role={'button'} onClick={() => doShowModal()}>
                        <FontAwesomeIcon icon={faCog} color={'#8A98A8'}/>
                    </div>
                </div>
            </div>
            {isDashboardItem ?
                <div>
                     {chartFunction()}
                </div>
                :
                <div style={{width: '100%', height: '50vh'}}>
                   <ResponsiveContainer width={'100%'} height={'100%'}>
                        {chartFunction()}
                    </ResponsiveContainer>
                </div>
            }
        </div>
    );
}